/**
 * Facebook (Meta) Pixel tracking utilities
 *
 * Dual-fires every event:
 *   - Browser pixel (fbq) — fast, but blocked by ad blockers / ITP
 *   - Conversions API via Cloud Function — server-to-server, not blockable
 *
 * Both calls share an event_id so Meta deduplicates them.
 *
 * Standard events: https://developers.facebook.com/docs/meta-pixel/reference
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseApp';
import { getExternalId, getFbBrowserId, getFbClickId, newOrderId } from './userIdentity';
import { isAnalyticsEnabled } from './isAnalyticsEnabled';

const fbCapiCallable = httpsCallable(functions, 'fbCapi');

const isFbqAvailable = () => typeof window !== 'undefined' && window.fbq;

const newEventId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const sendCapi = (eventName, eventId, customData) => {
    fbCapiCallable({
        eventName,
        eventId,
        sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        customData,
        userData: {
            fbp: getFbBrowserId(),
            fbc: getFbClickId(),
            externalId: getExternalId(),
        },
    }).catch((err) => {
        if (typeof console !== 'undefined') console.warn('CAPI send failed', err?.message);
    });
};

const track = (eventName, customData = {}, { withOrderId = false } = {}) => {
    // One gate covers both the browser pixel and the server-side CAPI
    // call — we want neither path active when running on localhost or in
    // a non-production build.
    if (!isAnalyticsEnabled()) return;
    const eventId = newEventId();
    const enriched = withOrderId ? { ...customData, order_id: newOrderId() } : customData;
    if (isFbqAvailable()) {
        window.fbq('track', eventName, enriched, { eventID: eventId });
    }
    sendCapi(eventName, eventId, enriched);
};

export const trackFbPageView = () => track('PageView');

export const trackFbAddToCart = (productName, sourceLabel) =>
    track(
        'AddToCart',
        {
            content_name: `${productName} - ${sourceLabel}`,
            content_category: productName,
            content_type: 'product',
            value: 0.5,
            currency: 'USD',
        },
        { withOrderId: true }
    );

export const trackFbDownload = (mapType) =>
    track(
        'Lead',
        {
            content_name: `Download - ${mapType}`,
            content_category: 'Download',
        },
        { withOrderId: true }
    );

export const trackFbImageUpload = () => track('CustomizeProduct');

export const trackFbStatsConfigChange = (mapType, position) =>
    track('CustomizeProduct', {
        content_name: `Stats overlay - ${mapType}`,
        content_category: 'StatsOverlay',
        custom_data: { position },
    });

export const trackFbShare = (mapType, network) => {
    const eventId = newEventId();
    const customData = {
        content_name: `Share - ${mapType}`,
        content_category: 'Share',
        share_network: network,
    };
    if (isFbqAvailable()) {
        window.fbq('trackCustom', 'Share', customData, { eventID: eventId });
    }
    sendCapi('Share', eventId, customData);
};

